import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  GetApp as GetAppIcon,
} from '@mui/icons-material';
import { getNonSubmittedApplications, getApplicationFilterOptions, getSubmittedApplications, getBlockedApplications, getAdminStatistics, getDashboardCountsByLabType, extendApplication } from '../../services/api';
import { LoaderWrapper } from '../../components/index';
import { shouldDisableDropdown } from '../../utils/labTypeFilter';
import * as XLSX from 'xlsx';

// Configure dayjs
dayjs.extend(customParseFormat);

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [TotalCount, setTotalCount] = useState(0);
  const [currentTab, setCurrentTab] = useState(1); // 1: Submitted, 2: Non-Submitted
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filters
  const [labTypeFilter, setLabTypeFilter] = useState(null);
  const [assessmentTypeFilter, setAssessmentTypeFilter] = useState(null);
  const [usernameFilter, setUsernameFilter] = useState(null);
  const [labIdFilter, setLabIdFilter] = useState(null);

  // Options for dropdowns
  const [labTypeOptions, setLabTypeOptions] = useState([]);
  const [assessmentTypeOptions, setAssessmentTypeOptions] = useState([]);
  const [usernameOptions, setUsernameOptions] = useState([]);
  const [labIdOptions, setLabIdOptions] = useState([]);

  const [stats, setStats] = useState({
    submitted: 0,
    nonSubmitted: 0,
    blocked: 0,
    total: 0,
    underReview: 0,
    scrutiny: 0,
    pendingReview: 0,
    finance: '0.00',
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);

  // Extend Date Dialog State
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [selectedExtendApp, setSelectedExtendApp] = useState(null);
  const [extendedDate, setExtendedDate] = useState(null);
  const [extendedRemarks, setExtendedRemarks] = useState('');
  const [extending, setExtending] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [exporting, setExporting] = useState(false);
  const [filterOptions, setFilterOptions] = useState([]);

  // Allowed lab type IDs from localStorage (same source as ScopeMaster)
  const allowedLabTypeIds = useMemo(() => {
    const ids = localStorage.getItem('labTypeIds');
    const id = localStorage.getItem('labTypeId');
    if (ids) return ids.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n));
    if (id) return [Number(id)];
    return [];
  }, []);

  // Filter API lab type options to only user's allowed types
  const filteredLabTypeOptions = useMemo(() => {
    const opts = filterOptions?.labTypes || [];
    if (!allowedLabTypeIds.length) return opts;
    return opts.filter(o => allowedLabTypeIds.includes(Number(o.LabTypeId)));
  }, [filterOptions, allowedLabTypeIds]);

  // Auto-set labTypeFilter to first allowed option when options first load
  const labTypeAutoSet = useRef(false);
  useEffect(() => {
    if (labTypeAutoSet.current) return;
    if (!filteredLabTypeOptions.length) return;
    labTypeAutoSet.current = true;
    setLabTypeFilter(filteredLabTypeOptions[0]);
  }, [filteredLabTypeOptions]);

  const fetchFilterOptions = async () => {
    try {
      const res = await getApplicationFilterOptions({
        type: currentTab
      });

      if (res.data.success) {
        setFilterOptions(res.data.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, [currentTab]);

  useEffect(() => {
    setLabTypeFilter(filteredLabTypeOptions.length > 0 ? filteredLabTypeOptions[0] : null);
    setAssessmentTypeFilter(null);
    setUsernameFilter(null);
    setLabIdFilter(null);
  }, [currentTab]);


  useEffect(() => {
    // Wait for labTypeFilter to be set when user has restricted lab types
    if (allowedLabTypeIds.length > 0 && !labTypeFilter) return;
    setPage(0);
    fetchApplications(0, rowsPerPage);
  }, [currentTab, labTypeFilter, assessmentTypeFilter, usernameFilter, labIdFilter]);

  useEffect(() => {
    // setFilteredApplications(applications);
    // Extract unique lab types and assessment types from applications
    if (applications.length > 0) {
      const uniqueLabTypes = [...new Set(applications.map(app => app.LabType).filter(Boolean))];
      const uniqueAssessmentTypes = [...new Set(applications.map(app => app.AssessmentType).filter(Boolean))];
      const uniqueUsernames = [...new Set(applications.map(app => app.LoginName).filter(Boolean))];
      const uniqueLabIds = [...new Set(applications.map(app => app.ApplicationNo).filter(Boolean))];

      setLabTypeOptions(uniqueLabTypes);
      setAssessmentTypeOptions(uniqueAssessmentTypes);
      setUsernameOptions(uniqueUsernames);
      setLabIdOptions(uniqueLabIds);
    }
  }, [applications]);

  const fetchApplications = async (pageParam = page, limitParam = rowsPerPage) => {
    try {
      setLoading(true);

      const filters = {
        labTypeId: labTypeFilter?.LabTypeId || '',
        assessmentTypeId: assessmentTypeFilter?.AssessmentTypeId || '',
        userName: usernameFilter?.LoginName || '',
        labId: labIdFilter?.ApplicationNo || '',
        page: pageParam + 1,
        limit: limitParam
      };

      let response;
      if (currentTab === 1) {
        // Fetch submitted applications (Mode 67)
        response = await getSubmittedApplications(filters);
      } else if (currentTab === 2) {
        // Fetch non-submitted applications (Mode 2)
        response = await getNonSubmittedApplications(filters);
      } else if (currentTab === 3) {
        // Fetch blocked applications (where isblock = 1)
        response = await getBlockedApplications(filters);
      }

      if (response && response.data.success) {
        const apps = response.data.data || [];
        setApplications(apps);
        // setFilteredApplications(apps);

        setTotalCount(response.data.totalCount);
      }

      // Fetch statistics using stored procedure (Mode 5 for Medical)
      const statsResponse = await getAdminStatistics();

      if (statsResponse.data.success) {
        const statsData = statsResponse.data.data;
        setStats({
          submitted: statsData.submitted || 0,
          nonSubmitted: statsData.nonSubmitted || 0,
          total: statsData.total || 0,
          blocked: statsData.blocked || 0,
          underReview: statsData.underReview || 0,
          scrutiny: statsData.scrutiny || 0,
          pendingReview: statsData.pendingReview || 0,
          finance: statsData.finance || '0.00',
        });
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchApplications(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);

    fetchApplications(0, newLimit); // ✅ ADD THIS
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleClearFilters = () => {
    setLabTypeFilter(filteredLabTypeOptions.length > 0 ? filteredLabTypeOptions[0] : null);
    setAssessmentTypeFilter(null);
    setUsernameFilter(null);
    setLabIdFilter(null);
  };

  const handleMenuOpen = (event, app) => {
    setAnchorEl(event.currentTarget);
    setSelectedApp(app);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedApp(null);
  };

  const handleViewDetails = (app) => {
    // Navigate to application details or open modal
    console.log('View details for:', app);
    handleMenuClose();
  };

  const handleBlockApplication = async (app) => {
    // Call API to block application
    console.log('Block application:', app);
    handleMenuClose();
    // Refresh data after action
    fetchApplications();
  };

  const handleExtendApplication = (app) => {
    setSelectedExtendApp(app);
    setExtendDialogOpen(true);
    setExtendedDate(null);
    setExtendedRemarks('');
  };

  const handleExtendDialogClose = () => {
    setExtendDialogOpen(false);
    setSelectedExtendApp(null);
    setExtendedDate(null);
    setExtendedRemarks('');
  };

  const handleSubmitExtension = async () => {
    if (!extendedDate || !extendedRemarks.trim()) {
      setErrorMessage('Please select a date and provide remarks');
      return;
    }

    if (!extendedDate || !dayjs.isDayjs(extendedDate) || extendedDate.isBefore(dayjs(), 'day') || extendedDate.isSame(dayjs(), 'day')) {
      setErrorMessage('Extended date must be greater than today');
      return;
    }

    // Calculate days being extended (from today to selected date)
    const daysToExtend = extendedDate.startOf('day').diff(dayjs().startOf('day'), 'day');

    // Get current extended days (default to 0 if not available)
    const currentExtendedDays = selectedExtendApp.extendedDays || 0;

    // Calculate total extended days after this extension
    const totalExtendedDays = currentExtendedDays + daysToExtend;

    // Check if total extended days would exceed 15 days limit
    if (totalExtendedDays > 15) {
      const remainingDays = 15 - currentExtendedDays;
      setErrorMessage(`Cannot extend beyond 15 days total. You have already extended ${currentExtendedDays} days. Maximum ${remainingDays} days can be extended.`);
      return;
    }

    try {
      setExtending(true);
      const response = await extendApplication({
        appId: selectedExtendApp.AppId,
        orgId: selectedExtendApp.OrgId,
        extendedDate: extendedDate.format('YYYY-MM-DD'), // Format as YYYY-MM-DD
        extendedRemarks: extendedRemarks.trim(),
        extendedBy: localStorage.getItem('orgId'),
        extendedDays: daysToExtend // Send the days being extended
      });

      if (response.data.success) {
        const { data: responseData } = response.data;
        let message = response.data.message || 'Application extended successfully!';
        if (responseData) {
          message = `Application extended successfully! Extended ${responseData.currentExtension} days. Total: ${responseData.totalExtendedDays}/15 days. Remaining: ${responseData.remainingDays} days.`;
        }
        setSuccessMessage(message);
        handleExtendDialogClose();
        fetchApplications(); // Refresh the data
      } else {
        setErrorMessage(response.data.message || 'Failed to extend application');
      }
    } catch (error) {
      console.error('Error extending application:', error);
      setErrorMessage('Error extending application: ' + (error.response?.data?.message || error.message));
    } finally {
      setExtending(false);
    }
  };

  const getStatusChip = (status, submitDate) => {
    if (status === 'Blocked') {
      return <Chip label="Blocked" color="error" size="small" icon={<BlockIcon />} />;
    } else if (status === 'Submitted' || submitDate) {
      return <Chip label="Submitted" color="success" size="small" icon={<CheckCircleIcon />} />;
    } else {
      return <Chip label="Non-Submitted" color="warning" size="small" icon={<HourglassEmptyIcon />} />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const decodeHtmlEntities = (text) => {
    if (!text) return text;
    const textArea = document.createElement('textarea');
    textArea.innerHTML = text;
    return textArea.value;
  };

const exportToExcel = async () => {

  setExporting(true);

  try {

    const filters = {
      labTypeId: labTypeFilter?.LabTypeId || '',
      assessmentTypeId: assessmentTypeFilter?.AssessmentTypeId || '',
      userName: usernameFilter?.LoginName || '',
      labId: labIdFilter?.ApplicationNo || '',
      page: 1,
      limit: 100000 // fetch all data
    };

    let response;

    if (currentTab === 1) {

      response = await getSubmittedApplications(filters);

    } else if (currentTab === 2) {

      response = await getNonSubmittedApplications(filters);

    } else if (currentTab === 3) {

      response = await getBlockedApplications(filters);
    }

    const allApplications = response?.data?.data || [];

    if (allApplications.length === 0) {

      setErrorMessage('No data available to export');
      return;
    }

    const tabNames = {
      1: 'Submitted Applications',
      2: 'Non-Submitted Applications',
      3: 'Blocked Applications'
    };

    const exportData = allApplications.map((app, index) => {

      const baseData = {

        'S. No': index + 1,
        'Application No': app.ApplicationNo || 'N/A',
        'Laboratory Name':
          app.LaboratoryName ||
          app.OrgName ||
          'N/A',

        'Username':
          app.LoginName || 'N/A',

        'Lab Type':
          app.LabType || 'N/A',

        'Assessment Type':
          app.AssessmentType || 'N/A',

        'Contact Person':
          app.ContactPerson || 'N/A',

        'Contact Email':
          app.ContactEmail ||
          app.Email ||
          'N/A',

        'Contact Mobile':
          app.ContactMobile ||
          app.Phone ||
          'N/A',

        'Address':
          decodeHtmlEntities(app.Address) ||
          'N/A',

        'City':
          decodeHtmlEntities(app.City) ||
          'N/A',

        'State':
          decodeHtmlEntities(app.State) ||
          'N/A',

        'Created On':
          formatDate(app.InsertDate)
      };

      if (currentTab === 2) {

        baseData['Extended Date'] =
          app.ExtendedDate || 'N/A';

        baseData['Extended Remarks'] =
          app.extendedRemarks || 'N/A';

      } else if (currentTab === 3) {

        baseData['Block Date'] =
          formatDate(app.BlockDate);
      }

      return baseData;
    });

    const ws =
      XLSX.utils.json_to_sheet(exportData);

    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      ws,
      tabNames[currentTab]
    );

    const timestamp =
      new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, '-');

    const filename =
      `${tabNames[currentTab].replace(/ /g, '_')}_${timestamp}.xlsx`;

    XLSX.writeFile(wb, filename);

    setSuccessMessage(
      `Excel exported successfully`
    );

  } catch (error) {

    console.error(
      'Error exporting:',
      error
    );

    setErrorMessage(
      'Failed to export Excel'
    );

  } finally {

    setExporting(false);
  }
};

  if (loading) {
    return <LoaderWrapper loading={true} type="page" message="Loading applications..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#00024c' }}>
        Admin Dashboard
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {stats.total}
              </Typography>
              <Typography variant="body2">Total Applications </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {stats.submitted}
              </Typography>
              <Typography variant="body2">Submitted Applications</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #f2994a 0%, #f2c94c 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {stats.nonSubmitted}
              </Typography>
              <Typography variant="body2">Non-Submitted Applications</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {stats.blocked}
              </Typography>
              <Typography variant="body2">Blocked Applications</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Applications Table */}
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2 }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab label="Submitted Applications" value={1} />
            <Tab label="Non-Submitted Applications" value={2} />
            <Tab label="Blocked Applications" value={3} />
          </Tabs>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<GetAppIcon />}
              onClick={exportToExcel}
              size="small"
              variant="outlined"
              color="primary"
              disabled={exporting || applications.length === 0}
            >
              {exporting ? 'Exporting...' : 'Export to Excel'}
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchApplications}
              size="small"
            >
              Refresh
            </Button>
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={filteredLabTypeOptions}
                getOptionLabel={(o) => o.LabType || ''}
                value={labTypeFilter}
                onChange={(event, newValue) => setLabTypeFilter(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Lab Type"
                    placeholder="Type or select lab type"
                  />
                )}
                isOptionEqualToValue={(option, value) => option?.LabTypeId === value?.LabTypeId}
                clearOnEscape
                disabled={shouldDisableDropdown(filteredLabTypeOptions)}
                disableClearable={shouldDisableDropdown(filteredLabTypeOptions)}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={filterOptions.assessmentTypes}
                getOptionLabel={(o) => o.AssessmentType || ''}
                onChange={(event, newValue) => setAssessmentTypeFilter(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assessment Type"
                    placeholder="Type or select assessment type"
                  />
                )}
                isOptionEqualToValue={(option, value) => option === value}
                clearOnEscape
                disableClearable={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={filterOptions.usernames}
                getOptionLabel={(o) => o.LoginName || ''}
                onChange={(event, newValue) => setUsernameFilter(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Username"
                    placeholder="Type or select username"
                  />
                )}
                isOptionEqualToValue={(option, value) => option === value}
                clearOnEscape
                disableClearable={false}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={filterOptions.labIds}
                getOptionLabel={(o) => o.ApplicationNo || ''}
                onChange={(event, newValue) => setLabIdFilter(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Lab ID"
                    placeholder="Type or select lab ID"
                  />
                )}
                isOptionEqualToValue={(option, value) => option === value}
                clearOnEscape
                disableClearable={false}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearFilters}
                startIcon={<FilterListIcon />}
              >
                Clear Filters
              </Button>
            </Grid>
            {currentTab === 3 && (
              <Grid item xs={12}>
                <Alert severity="error"> A maximum extension of 15 days will be allowed for the application.</Alert>
              </Grid>
            )}
          </Grid>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>S. No</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Laboratory Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 250 }}>Lab Info</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 220 }}>Contact Info</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Created On</TableCell>
                {currentTab === 2 && (
                  <>
                    <TableCell sx={{ fontWeight: 'bold' }}>Extend Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Extend Remarks</TableCell>
                  </>
                )}
                {currentTab === 3 && (
                  <TableCell sx={{ fontWeight: 'bold' }}>Block Date</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {applications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={currentTab === 3 ? 12 : 10} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No applications found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                applications.map((app, index) => (
                  <TableRow
                    key={app.AppId || index}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell align="center">
                      {page * rowsPerPage + index + 1}<br />
                      {currentTab === 3 && (
                        <Button color="primary" variant='contained' size="small" onClick={() => handleExtendApplication(app)}>
                          Extend Date
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>{app.LaboratoryName || app.OrgName || 'N/A'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {app.LoginName || 'N/A'}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {app.ApplicationNo && (
                            <Chip
                              label={app.ApplicationNo}
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                          {app.LabType && (
                            <Chip
                              label={app.LabType}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                          {app.AssessmentType && (
                            <Chip
                              label={app.AssessmentType}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                          {app.ContactPerson || 'N/A'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {(app.ContactEmail || app.Email) && (
                            <Chip
                              label={app.ContactEmail || app.Email}
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ fontSize: '0.65rem', height: 20 }}
                            />
                          )}
                          {(app.ContactMobile || app.Phone) && (
                            <Chip
                              label={app.ContactMobile || app.Phone}
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ fontSize: '0.65rem', height: 20 }}
                            />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {app.Address && (
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                            {decodeHtmlEntities(app.Address)}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {app.City && (
                            <Chip
                              label={decodeHtmlEntities(app.City)}
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                          {app.State && (
                            <Chip
                              label={decodeHtmlEntities(app.State)}
                              size="small"
                              color="default"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(app.InsertDate)}</TableCell>
                    {currentTab === 2 && (
                      <>
                        <TableCell>{app.ExtendedDate}</TableCell>
                        <TableCell>{app.extendedRemarks || 'N/A'}</TableCell>
                      </>
                    )}
                    {currentTab === 3 && (
                      <TableCell>{formatDate(app.BlockDate)}</TableCell>
                    )}

                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={TotalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleViewDetails(selectedApp)}>
          <VisibilityIcon sx={{ mr: 1, fontSize: 20 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => handleBlockApplication(selectedApp)}>
          <BlockIcon sx={{ mr: 1, fontSize: 20 }} />
          Extend date
        </MenuItem>
      </Menu>

      {/* Extend Date Dialog */}
      <Dialog
        open={extendDialogOpen}
        onClose={handleExtendDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'white',
            boxShadow: '0 24px 64px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            py: 3,
            textAlign: 'center'
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Extend Application Date
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
            {selectedExtendApp?.LaboratoryName || selectedExtendApp?.OrgName}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Grid container spacing={3}>
            {/* Extended Days Information */}
            <Grid item xs={12}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Extension Limit Information
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip
                    label={`Extended: ${selectedExtendApp?.extendedDays || 0} days`}
                    color="warning"
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`Remaining: ${15 - (selectedExtendApp?.extendedDays || 0)} days`}
                    color={15 - (selectedExtendApp?.extendedDays || 0) > 0 ? 'success' : 'error'}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label="Max: 15 days total"
                    color="info"
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Alert>
            </Grid>
            <Grid item xs={12} mt={1}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Extended Date"
                  value={extendedDate}
                  onChange={(newValue) => setExtendedDate(newValue)}
                  minDate={dayjs().add(1, 'day')} // Tomorrow onwards
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      helperText: "Select a date greater than today (dd/mm/yyyy)",
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            {/* Dynamic Extension Calculation */}
            {extendedDate && (
              <Grid item xs={12}>
                <Alert
                  severity={(() => {
                    const daysToExtend = extendedDate.startOf('day').diff(dayjs().startOf('day'), 'day');
                    const currentExtendedDays = selectedExtendApp?.extendedDays || 0;
                    const totalDays = currentExtendedDays + daysToExtend;
                    return totalDays > 15 ? 'error' : totalDays > 3 ? 'warning' : 'success';
                  })()}
                  sx={{ borderRadius: 2 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Extension Calculation
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography variant="body2">
                      Extending by: <strong>{extendedDate.startOf('day').diff(dayjs().startOf('day'), 'day')} days</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>•</Typography>
                    <Typography variant="body2">
                      Total after extension: <strong>{(selectedExtendApp?.extendedDays || 0) + extendedDate.startOf('day').diff(dayjs().startOf('day'), 'day')} / 15 days</strong>
                    </Typography>
                    {/* {(selectedExtendApp?.extendedDays || 0) + extendedDate.startOf('day').diff(dayjs().startOf('day'), 'day') > 15 && (
                      <>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>•</Typography>
                        <Typography variant="body2" color="error">
                          <strong>Exceeds limit!</strong>
                        </Typography>
                      </>
                    )} */}
                  </Box>
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={4}
                label="Extension Remarks"
                value={extendedRemarks}
                onChange={(e) => setExtendedRemarks(e.target.value)}
                placeholder="Please provide reason for extending the application date..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleExtendDialogClose}
            disabled={extending}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              color: '#666'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitExtension}
            disabled={extending || !extendedDate || !extendedRemarks.trim() || (() => {
              if (!extendedDate) return false;
              const daysToExtend = extendedDate.startOf('day').diff(dayjs().startOf('day'), 'day');
              const currentExtendedDays = selectedExtendApp?.extendedDays || 0;
              const totalDays = currentExtendedDays + daysToExtend;
              return totalDays > 15;
            })()}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 4,
              '&:hover': {
                background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
              },
              '&:disabled': {
                background: '#ccc'
              }
            }}
          >
            {extending ? 'Extending...' : 'Extend Application'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Message */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')} sx={{ borderRadius: 2 }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Message */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMessage('')} sx={{ borderRadius: 2 }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminDashboard;
